import React, { useEffect, useState } from 'react';
import { MessageCircle, TrendingUp, Clock, Calendar, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import api from '../utils/api';

const RANGES = [
  { days: 7, label: '7 días' },
  { days: 30, label: '30 días' },
  { days: 90, label: '90 días' },
];

const WhatsAppAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get(`/whatsapp-analytics/report?days=${days}`);
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Error cargando reporte');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [days]);

  const maxClicks = data?.top_estudios?.[0]?.clicks || 1;

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-analytics-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 text-green-600 flex items-center justify-center">
              <MessageCircle className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-xl text-[#1e3a8a]">Analytics de WhatsApp</CardTitle>
              <p className="text-sm text-gray-500 mt-0.5">
                Clics en el botón flotante — estudios más consultados
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.days}
                variant={days === r.days ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDays(r.days)}
                data-testid={`wa-range-${r.days}`}
              >
                {r.label}
              </Button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Cargando reporte…
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-600 bg-red-50 rounded-lg border border-red-200">
              {error}
            </div>
          ) : (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-5" data-testid="wa-kpi-total">
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <MessageCircle className="h-4 w-4" />
                    Total de clics
                  </div>
                  <div className="text-3xl font-bold mt-2">{data?.total || 0}</div>
                  <div className="text-xs opacity-75 mt-1">últimos {days} días</div>
                </div>

                <div className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white rounded-xl p-5">
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <TrendingUp className="h-4 w-4" />
                    Top estudio
                  </div>
                  <div className="text-lg font-bold mt-2 line-clamp-1">
                    {data?.top_estudios?.[0]?.estudio || '—'}
                  </div>
                  <div className="text-xs opacity-75 mt-1">
                    {data?.top_estudios?.[0]?.clicks || 0} clics
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#84cc16] to-[#65a30d] text-white rounded-xl p-5">
                  <div className="flex items-center gap-2 text-sm opacity-90">
                    <Calendar className="h-4 w-4" />
                    Promedio diario
                  </div>
                  <div className="text-3xl font-bold mt-2">
                    {data?.total ? (data.total / days).toFixed(1) : '0'}
                  </div>
                  <div className="text-xs opacity-75 mt-1">clics/día</div>
                </div>
              </div>

              {/* Top Estudios */}
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-[#1e40af]" />
                  Estudios más consultados
                </h3>
                {!data?.top_estudios?.length ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg">
                    Aún no hay clics registrados en este periodo.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.top_estudios.map((item, i) => {
                      const pct = Math.round((item.clicks / maxClicks) * 100);
                      return (
                        <div key={item.estudio + i} className="group" data-testid={`wa-top-${i}`}>
                          <div className="flex items-center justify-between mb-1 text-sm">
                            <span className="font-medium text-gray-900">
                              <span className="text-gray-400 mr-2 tabular-nums">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              {item.estudio}
                            </span>
                            <span className="font-semibold text-[#1e40af] tabular-nums">
                              {item.clicks} {item.clicks === 1 ? 'clic' : 'clics'}
                            </span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-[#1e40af] to-[#84cc16] rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Actividad por día */}
              {data?.daily?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#1e40af]" />
                    Actividad por día
                  </h3>
                  <div className="flex items-end gap-1 h-24 bg-gray-50 rounded-lg p-3">
                    {data.daily.map((d) => {
                      const max = Math.max(...data.daily.map((x) => x.clicks), 1);
                      const h = Math.max((d.clicks / max) * 100, 4);
                      return (
                        <div
                          key={d.date}
                          className="flex-1 bg-[#1e40af] rounded-t hover:bg-[#84cc16] transition-colors relative group min-w-[4px]"
                          style={{ height: `${h}%` }}
                          title={`${d.date}: ${d.clicks} clics`}
                        >
                          <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] bg-gray-900 text-white px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                            {d.date}: {d.clicks}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Últimos clics */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[#1e40af]" />
                  Últimos clics
                </h3>
                {!data?.recent?.length ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg">
                    Sin actividad reciente.
                  </p>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                        <tr>
                          <th className="text-left px-4 py-2 font-medium">Estudio</th>
                          <th className="text-left px-4 py-2 font-medium">Página</th>
                          <th className="text-right px-4 py-2 font-medium">Fecha</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.recent.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium text-gray-900">{r.estudio}</td>
                            <td className="px-4 py-2 text-gray-500">{r.page || '—'}</td>
                            <td className="px-4 py-2 text-right text-gray-500 tabular-nums">
                              {formatDate(r.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppAnalyticsTab;
